import { useController } from '@framework/form/hooks/useController.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { message } from 'antd';
import { type MessageInstance } from 'antd/es/message/interface';
import { useCallback, useMemo } from 'react';
import { buildAsyncServiceParams } from '../../../../../../expressions/repository/async/buildAsyncServiceParams.ts';
import { useAsyncService } from '../../../../../../expressions/repository/async/useAsyncService.ts';
import {
  type IAsyncServiceInputConfig,
  useAsyncServiceFunction,
} from '../../../../../../expressions/repository/async/useAsyncServiceFetch.ts';
import { useAsyncServiceVaribles } from '../../../../../../expressions/repository/async/useAsyncServiceVariables.ts';
import { useWidgetScopeContext } from '../../../../../../form/fillable/WidgetScopeContext.tsx';
import { getActionDataObjectName } from '../../getActionDataObjectName.ts';
import { type IButtonWidget } from '../../IButtonWidget.ts';

/**
 * Recupera e normalizza il dato ritornato dall'API
 * e lo salva nel campo del form
 */
function parseResultToSave(data: any) {
  if (!data) return {};
  if (!Array.isArray(data)) return data;

  // Se è un array
  if (data.length === 0) return {};
  return data[0] ?? {};
}

interface IMessages {
  messageApi?: MessageInstance;
  enableRequestMessage?: boolean | null;
  requestMessage?: string | null;
  enableSuccessMessage?: boolean | null;
  successMessage?: string | null;
}

export function useExecAsyncServiceAction(params: {
  basePath: string;
  serviceId: string;
  inputs: IAsyncServiceInputConfig;
  widget: IButtonWidget;
  index: number;
  messages: IMessages;
}) {
  const { basePath, serviceId, inputs, widget, index, messages } = params;
  const { messageApi } = messages;

  const { service, axiosClient } = useAsyncService(serviceId);

  const handleFunction = useAsyncServiceFunction(service, axiosClient);

  const variables = useAsyncServiceVaribles(basePath, inputs);

  const { prefix } = useWidgetScopeContext();

  const objectFieldName = useMemo(
    () => `${prefix}${getActionDataObjectName(widget, index)}`,
    [index, prefix, widget],
  );

  const { field: objectField } = useController(objectFieldName);

  // Funzioni per gestire il messaggio di caricamento
  const showLoadingMessage = useCallback(() => {
    if (messages.enableRequestMessage) {
      messageApi?.loading(messages.requestMessage);
    }
  }, [messageApi, messages.enableRequestMessage, messages.requestMessage]);

  const hideLoadingMessage = useCallback(() => {
    if (messages.enableRequestMessage) {
      messageApi?.destroy();
    }
  }, [messageApi, messages.enableRequestMessage]);

  // Azione
  const handleAction = useCallback(async () => {
    if (!service || !inputs || !axiosClient) {
      // biome-ignore format: <tbd>
      console.warn(`[useExecAsyncServiceAction] Errore durante l'esecuzione del servizio asincrono ${serviceId}: service/inputs/axiosClient non definiti`, { service, inputs, axiosClient });
      return null;
    }

    if (!handleFunction) {
      // biome-ignore format: <tbd>
      message.error(t('widgets.use-exec-async-service-action.no-handle-function', 'Attesa caricamento del servizio asincrono. Riprovare.'));
      return null;
    }

    try {
      showLoadingMessage();
      const params = buildAsyncServiceParams(service, inputs, variables);

      const resultData = await handleFunction?.(params);

      objectField.onChange(parseResultToSave(resultData));

      // Nascondo il messaggio di loading e mostro quello di successo
      hideLoadingMessage();
      if (messages.enableSuccessMessage) {
        message.success(messages.successMessage);
      }
      return resultData ?? null;
    } catch (error: any) {
      // biome-ignore format: <tbd>
      console.warn(`Errore durante la chiamata al servizio ${service.name}`, error);
      // biome-ignore format: <tbd>
      message.error(t('widgets.use-exec-async-service-action.error-during-loading', 'Si è verificato un errore durante l\'esecuzione del servizio asincrono'));
    }
  }, [
    axiosClient,
    handleFunction,
    hideLoadingMessage,
    inputs,
    messages.enableSuccessMessage,
    messages.successMessage,
    objectField,
    service,
    serviceId,
    showLoadingMessage,
    variables,
  ]);

  return handleAction;
}
